import { Link, useFetcher } from "@remix-run/react";

import type { ProductSubscription } from "~/modules/subscription/type";
import { IntervalPrice } from "~/modules/subscription/type";
import { isFormProcessing } from "~/utils";

import KonnectButton from "../KonnectButton/KonnectButton";

interface ProductCardProps {
  product: ProductSubscription;
  intervalToggle?: IntervalPrice;
  showRecommended?: boolean;
  activePlanId?: string;
  canSubscribe?: boolean;
}

const ProductCard = ({
  product: { title, description, features, recommended: _recommended, pricing },
  intervalToggle = IntervalPrice.MONTHLY,
  showRecommended = true,
  activePlanId,
  canSubscribe = false,
}: ProductCardProps) => {
  const recommended = showRecommended && _recommended;

  const subscribeFetcher = useFetcher();
  const isProcessing = isFormProcessing(subscribeFetcher.state);
  const processingTierId = subscribeFetcher.formData?.get("tierId");

  const { price, interval, intervalCount, trial, id } = pricing[intervalToggle];

  const hightlighted = recommended || activePlanId === id;

  return (
    <div className="mx-auto flex size-full max-w-[450px] shrink-0 flex-col">
      {showRecommended && (
        <div
          className={`rounded-t-lg border ${
            recommended
              ? "border-purple bg-purple"
              : "hidden border-transparent bg-transparent lg:block"
          } py-[10px] text-center uppercase text-white`}
        >
          {recommended ? "Recommended" : "/"}
        </div>
      )}
      <div
        className={`mx-auto flex w-full grow flex-col rounded-lg border border-purple py-[25px] text-center ${
          hightlighted ? "rounded-t-none bg-charcoal text-white" : "bg-offWhite"
        }`}
      >
        <h3 className={`text-lg font-semibold ${hightlighted && `text-white`}`}>
          {title}
        </h3>
        <div className="mb-[20px]">
          <span className="text-3xl font-bold">
            ${price}
            <span className="text-base">+GST</span>
          </span>
          <div className="text-sm font-medium">
            per{" "}
            {interval === "month" && intervalCount === 3 ? "quarter" : "month"}
          </div>
          <div
            className={`mt-[10px] ${
              hightlighted
                ? "text-[rgba(255,255,255,0.5)]"
                : "text-[rgba(55,65,81,0.5)]"
            }`}
          >
            {String(intervalCount)}-Month Payment Intervals
          </div>
        </div>
        {trial && (
          <div className="mb-[20px] bg-[#DBD7F8] py-[10px] text-center text-purple">
            Free {trial}-day trial
          </div>
        )}
        <div className="flex grow flex-col items-center justify-between">
          <div className="w-full px-[25px]">
            <p className="mb-[20px] h-[70px]">{description}</p>
            <ul className="mb-1 space-y-[15px] text-left">
              {features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-center space-x-[15px] text-sm"
                >
                  <svg
                    className={`size-[14px] shrink-0`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  <span className="font-medium">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          {canSubscribe || (activePlanId && activePlanId !== id) ? (
            <subscribeFetcher.Form method="post" action="/api/stripe/subscribe">
              <input type="hidden" name="priceId" value={id} />
              <button
                disabled={isProcessing}
                className="mt-[20px] flex items-center rounded-lg bg-purple px-[15px] py-[10px] text-center font-semibold text-white transition hover:bg-charcoal"
              >
                <svg
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L5.89956 5.76444C5.9314 5.79536 5.95666 5.83208 5.9739 5.8725C5.99113 5.91292 6 5.95624 6 6C6 6.04376 5.99113 6.08708 5.9739 6.1275C5.95666 6.16792 5.9314 6.20464 5.89956 6.23556L1 11"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="ml-[10px]">
                  {id === processingTierId ? `Loading...` : `Choose this plan`}
                </span>
              </button>
            </subscribeFetcher.Form>
          ) : (
            <Link to={`/join?id=${id}`} prefetch="intent" className="mt-[20px]">
              <KonnectButton text={"Get started"} icon={true} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
